import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Save, 
  Mail, 
  AlertTriangle, 
  FileText, 
  RefreshCw,
  Info,
  Bell
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Template di default (constant fuori dal componente per evitare ricreazioni a ogni render)
const DEFAULT_TEMPLATES = {
  sollecito_pagamento: {
    nome: 'Sollecito Pagamento',
    oggetto: 'Sollecito pagamento canone - {mese}',
    corpo: `Gentile {nome_inquilino},

La informiamo che risulta ancora non pagato l'importo di € {importo} con scadenza {data_scadenza} relativo a: {descrizione}.

La preghiamo di provvedere al pagamento al più presto.

Cordiali saluti,
SemplificAffitto`,
    variabili: ['nome_inquilino', 'importo', 'data_scadenza', 'descrizione', 'mese', 'indirizzo_immobile']
  },
  avviso_scadenza_contratto: {
    nome: 'Avviso Scadenza Contratto',
    oggetto: 'Avviso scadenza contratto - {codice_contratto}',
    corpo: `Gentile {nome_proprietario},

La informiamo che il contratto {codice_contratto} relativo all'immobile in {indirizzo_immobile} scadrà il {data_scadenza}.

Si prega di prendere visione e valutare le opzioni di rinnovo o disdetta.

Cordiali saluti,
SemplificAffitto`,
    variabili: ['nome_proprietario', 'codice_contratto', 'indirizzo_immobile', 'data_scadenza']
  },
  avviso_disdetta: {
    nome: 'Avviso Termine Disdetta',
    oggetto: 'Termine per disdetta contratto - {codice_contratto}',
    corpo: `Gentile {nome_proprietario},

La informiamo che il termine per comunicare la disdetta del contratto {codice_contratto} scadrà il {data_scadenza}.

Se intende procedere con la disdetta, è necessario inviare comunicazione entro la data indicata.

Cordiali saluti,
SemplificAffitto`,
    variabili: ['nome_proprietario', 'codice_contratto', 'data_scadenza']
  },
  avviso_imposta_registro: {
    nome: 'Avviso Imposta di Registro',
    oggetto: 'Scadenza imposta di registro - {codice_contratto}',
    corpo: `Gentile {nome_proprietario},

La informiamo che la scadenza per il pagamento dell'imposta di registro annuale relativa al contratto {codice_contratto} è fissata per il {data_scadenza}.

L'importo dovuto è pari a € {importo}.

Cordiali saluti,
SemplificAffitto`,
    variabili: ['nome_proprietario', 'codice_contratto', 'data_scadenza', 'importo']
  },
  conferma_pagamento: {
    nome: 'Conferma Pagamento Ricevuto',
    oggetto: 'Conferma pagamento - {mese}',
    corpo: `Gentile {nome_inquilino},

Confermiamo la ricezione del pagamento di € {importo} relativo a: {descrizione} per il mese di {mese}.

Cordiali saluti,
SemplificAffitto`,
    variabili: ['nome_inquilino', 'importo', 'descrizione', 'mese']
  }
};

const TemplateTestiPage = () => {
  const [templates, setTemplates] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/templates`);
      // Merge con i default (per mostrare anche quelli non ancora salvati)
      const merged = { ...DEFAULT_TEMPLATES };
      if (res.data && typeof res.data === 'object') {
        Object.keys(res.data).forEach(key => {
          if (merged[key]) {
            merged[key] = { ...merged[key], ...res.data[key] };
          } else {
            merged[key] = res.data[key];
          }
        });
      }
      setTemplates(merged);
    } catch (error) {
      // Se non esiste ancora la collection, usa i default
      setTemplates(DEFAULT_TEMPLATES);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleSaveTemplate = async (templateKey) => {
    try {
      setSaving(true);
      await axios.put(`${API_URL}/templates/${templateKey}`, templates[templateKey]);
      toast.success('Template salvato con successo');
    } catch (error) {
      toast.error('Errore durante il salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleResetTemplate = (templateKey) => {
    setTemplates(prev => ({
      ...prev,
      [templateKey]: DEFAULT_TEMPLATES[templateKey]
    }));
    toast.info('Template ripristinato ai valori predefiniti (non ancora salvato)');
  };

  const updateTemplate = (templateKey, field, value) => {
    setTemplates(prev => ({
      ...prev,
      [templateKey]: {
        ...prev[templateKey],
        [field]: value
      }
    }));
  };

  const getIconForTemplate = (key) => {
    if (key.includes('sollecito')) return <AlertTriangle className="w-4 h-4 text-amber-500" />;
    if (key.includes('avviso')) return <Bell className="w-4 h-4 text-orange-500" />;
    if (key.includes('conferma')) return <Mail className="w-4 h-4 text-green-500" />;
    return <FileText className="w-4 h-4 text-slate-500" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <div data-testid="template-testi-page" className="p-6">
      <div className="page-header mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Template Comunicazioni</h1>
            <p className="text-slate-500 mt-1">Personalizza i testi delle comunicazioni automatiche</p>
          </div>
          <Button 
            variant="outline" 
            onClick={fetchTemplates}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Ricarica
          </Button>
        </div>
      </div>

      {/* Info Box */}
      <Card className="mb-6 bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900">Come usare le variabili</p>
              <p className="text-sm text-blue-700 mt-1">
                Inserisci le variabili tra parentesi graffe, es: <code className="bg-blue-100 px-1 rounded">{'{nome_inquilino}'}</code>. 
                Le variabili verranno sostituite automaticamente con i dati reali al momento dell'invio.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Templates */}
      <Tabs defaultValue="sollecito_pagamento" className="space-y-4">
        <TabsList className="flex-wrap h-auto gap-2 bg-slate-100 p-2">
          {Object.keys(templates).map((key) => (
            <TabsTrigger 
              key={key} 
              value={key}
              className="flex items-center gap-2 data-[state=active]:bg-white"
            >
              {getIconForTemplate(key)}
              {templates[key].nome}
            </TabsTrigger>
          ))}
        </TabsList>

        {Object.keys(templates).map((key) => (
          <TabsContent key={key} value={key}>
            <Card>
              <CardContent className="p-6 space-y-6">
                {/* Header Template */}
                <div className="flex items-center justify-between pb-4 border-b">
                  <div className="flex items-center gap-3">
                    {getIconForTemplate(key)}
                    <div>
                      <h3 className="font-semibold text-lg">{templates[key].nome}</h3>
                      <p className="text-sm text-slate-500">Modifica il testo di questo template</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleResetTemplate(key)}
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Ripristina Default
                    </Button>
                    <Button 
                      size="sm"
                      onClick={() => handleSaveTemplate(key)}
                      disabled={saving}
                      className="bg-slate-900 hover:bg-slate-800"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Salva Template
                    </Button>
                  </div>
                </div>

                {/* Variabili disponibili */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-slate-700 mb-2">Variabili disponibili:</p>
                  <div className="flex flex-wrap gap-2">
                    {[...new Set([...(templates[key].variabili || []), 'iban_edilstudio'])].map((v) => (
                      <code 
                        key={v} 
                        className="px-2 py-1 bg-white border rounded text-xs text-slate-600 cursor-pointer hover:bg-slate-100"
                        onClick={() => navigator.clipboard.writeText(`{${v}}`).then(() => toast.info(`Copiato: {${v}}`))}
                        title="Clicca per copiare"
                      >
                        {`{${v}}`}
                      </code>
                    ))}
                  </div>
                </div>

                {/* Oggetto */}
                <div>
                  <Label htmlFor={`oggetto-${key}`}>Oggetto Email</Label>
                  <Input
                    id={`oggetto-${key}`}
                    value={templates[key].oggetto || ''}
                    onChange={(e) => updateTemplate(key, 'oggetto', e.target.value)}
                    placeholder="Oggetto della comunicazione..."
                    className="mt-1"
                    data-testid={`input-oggetto-${key}`}
                  />
                </div>

                {/* Corpo */}
                <div>
                  <Label htmlFor={`corpo-${key}`}>Corpo del Messaggio</Label>
                  <Textarea
                    id={`corpo-${key}`}
                    value={templates[key].corpo || ''}
                    onChange={(e) => updateTemplate(key, 'corpo', e.target.value)}
                    placeholder="Scrivi il testo del messaggio..."
                    rows={12}
                    className="mt-1 font-mono text-sm"
                    data-testid={`input-corpo-${key}`}
                  />
                </div>

                {/* Anteprima */}
                <div>
                  <Label>Anteprima</Label>
                  <div className="mt-2 p-4 bg-white border rounded-lg">
                    <p className="font-medium text-slate-900 mb-2 pb-2 border-b">
                      {templates[key].oggetto?.replace(/\{[^}]+\}/g, '[...]') || 'Oggetto...'}
                    </p>
                    <pre className="whitespace-pre-wrap text-sm text-slate-700 font-sans">
                      {templates[key].corpo?.replace(/\{[^}]+\}/g, '[...]') || 'Corpo messaggio...'}
                    </pre>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

export default TemplateTestiPage;
